"use client";

import React, { useState, useEffect } from "react";
import { Button } from "../../ui/button";
import { useArchivalMode } from "@/contexts/ArchivalModeContext";
import { getFY_YEAR } from "@/utils/sharepoint-env";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Label } from "../../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Input } from "../../ui/input";
import { CheckCircle, AlertCircle, X, Download, Play, File, Cloud, Loader2, Database, Settings, Search, Upload, ArrowLeft, MessageSquare, Save } from "lucide-react";
import { ReviewComment } from "../../shared/ReviewComment";
import { DocumentManager } from "../../planning/DocumentManager";
import { getCompanyName } from "@/lib/textron-data-processor";
import { 
  getProcedureFileUsage, 
  updateProcedureFileUsage,
  type FileUsageEntry 
} from "@/utils/fileUsageReference";

interface IPETestingProps {
  onBack?: () => void;
}

interface ExcelColumn {
  name: string;
  value: string;
}

const FIELD_KEYS: Array<{ key: string; label: string }> = [
  { key: "employee_code", label: "Employee Code" },
  { key: "employee_name", label: "Employee Name" },
  { key: "designation", label: "Designation" },
  { key: "pay_month", label: "Pay Month" },
  { key: "date_of_joining", label: "Date of Joining" },
  { key: "date_of_leaving", label: "Date of Leaving" },
  { key: "pan", label: "PAN Number" },
  { key: "gross_pay", label: "Gross Pay" },
  { key: "net_pay", label: "Net Pay" },
  { key: "total_deductions", label: "Total Deductions" },
  { key: "pf", label: "Provident Fund" },
  { key: "esi", label: "ESI" },
];

export default function IPETesting({ onBack }: IPETestingProps) {
  const { isArchivalMode } = useArchivalMode();
  // State management
  const [excelColumns, setExcelColumns] = useState<ExcelColumn[]>([]);
  const [isLoadingExcelColumns, setIsLoadingExcelColumns] = useState(false);
  const [selectedFile, setSelectedFile] = useState<string>("");
  const [columnMappings, setColumnMappings] = useState<Record<string, string>>({});
  const [processingStatus, setProcessingStatus] = useState<"idle" | "running" | "completed" | "error">("idle");
  const [fileSearchStatus, setFileSearchStatus] = useState<"idle" | "searching" | "found" | "not_found">("idle");
  const [foundFiles, setFoundFiles] = useState<string[]>([]);
  const [referenceValue, setReferenceValue] = useState<string>("");
  const [uploadStatus, setUploadStatus] = useState<"idle" | "uploading" | "completed" | "error">("idle");
  const [isExecutingIPE, setIsExecutingIPE] = useState(false);
  const [reviewCommentQuestionId, setReviewCommentQuestionId] = useState<string | null>(null);
  const [fileUsageReference, setFileUsageReference] = useState<FileUsageEntry[]>([]);
  const [companyName, setCompanyName] = useState<string>("");
  const [inferenceQuestion, setInferenceQuestion] = useState<any>({ textResponse: null, documents: [] });
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);

  // Generate question ID for a section
  const getSectionQuestionId = (sectionName: string): string => {
    const normalizedSection = sectionName
      .replace(/[^a-zA-Z0-9\s]/g, "")
      .replace(/\s+/g, "_")
      .toUpperCase();
    return `IPE_TESTING_${normalizedSection}`;
  };

  const searchForFiles = async () => {
    setFileSearchStatus("searching");
    try {
      if (window.sharePointAPI?.loadComcFiles) {
        const result = await window.sharePointAPI.loadComcFiles("Execution_EBE_PayRegistrar");
        if (result.success && result.data?.files) {
          const files = result.data.files
            .map((f: any) => f.name)
            .filter((name: string) => name && name.trim().length > 0);
          if (files.length > 0) {
            setFoundFiles(files);
            setFileSearchStatus("found");
          } else {
            setFileSearchStatus("not_found");
          }
        } else {
          setFileSearchStatus("not_found");
        }
      } else {
        setFileSearchStatus("not_found");
      }
    } catch {
      setFileSearchStatus("not_found");
    }
  };

  // Load company name
  useEffect(() => {
    const loadCompanyName = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");

      } catch (error) {
        console.warn('Failed to load company name, using fallback');
        setCompanyName("ABC Private Limited");
      }
    };
    loadCompanyName();
  }, []);

  // Load inference question from Execution_EBE_Responses.json
  useEffect(() => {
    const loadInferenceQuestion = async () => {
      try {
        const result = await (window as any).sharePointAPI?.downloadClientJson?.("Execution_EBE_Responses.json");
        if (result?.success && result?.data?.substantiveProcedures) {
          const inferenceId = "IPE-TESTING-INFERENCE";
          const inferenceData = result.data.substantiveProcedures[inferenceId];
          if (inferenceData) {
            setInferenceQuestion({
              textResponse: inferenceData.textResponse || null,
              documents: (inferenceData.documents || []).map((docName: string) => ({
                id: `doc_${docName}`,
                type: 'linked',
                name: docName,
              })),
            });
          }
        }
      } catch (error) {
        console.warn("Failed to load inference question:", error);
      }
    };
    loadInferenceQuestion();
  }, []);

  // Auto-trigger search for files when component mounts (when user clicks on IPE Testing panel)
  useEffect(() => {
    searchForFiles();
    loadFileUsageReference();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadFileUsageReference = async () => {
    try {
      const usage = await getProcedureFileUsage("IPE Testing");
      setFileUsageReference(usage);
    } catch (error) {
      console.error("Error loading file usage reference:", error);
    }
  };

  const uploadFile = async () => {
    setUploadStatus("uploading");
    try {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.xlsx,.xls,.csv';
      input.onchange = async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          try {
            const base64Content = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => {
                const result = reader.result as string;
                const base64 = result.split(',')[1];
                resolve(base64);
              };
              reader.onerror = () => reject(new Error('Failed to read file'));
              reader.readAsDataURL(file);
            });

            if (window.sharePointAPI?.uploadFile) {
              // Get FY_YEAR from database dynamically
              const fyYear = getFY_YEAR();
              if (!fyYear) {
                throw new Error("FY_YEAR is not configured. Please ensure you have selected an engagement.");
              }

              const result = await window.sharePointAPI.uploadFile({
                fileContent: base64Content,
                fileName: file.name,
                referenceValue: referenceValue,
                folderName: "client",
                fyYear: fyYear,
                sectionList: ["Execution_EBE_PayRegistrar"],
              });

              if (result.success) {
                setSelectedFile(file.name);
                setFoundFiles([file.name]);
                setFileSearchStatus("found");
                setUploadStatus("completed");
              } else {
                setUploadStatus("error");
              }
            } else {
              setUploadStatus("error");
            }
          } catch {
            setUploadStatus("error");
          }
        }
      };
      input.click();
    } catch {
      setUploadStatus("error");
    }
  };

  const loadExcelColumns = async () => {
    if (!selectedFile) return;
    setIsLoadingExcelColumns(true);
    try {
      if (window.payroll?.loadExcelColumns) {
        const result = await window.payroll.loadExcelColumns(selectedFile);
        if (result.ok && result.columns) {
          const columns = result.columns.map((col: string) => ({ name: col, value: col }));
          setExcelColumns(columns);
        }
      } else {
        const testColumns = [
          "Employee Code", "Employee Name", "Designation", "Pay Month",
          "Date of Joining", "Date of Leaving", "PAN Number", "Gross Pay",
          "Net Pay", "Total Deductions", "Provident Fund", "ESI"
        ];
        const columns = testColumns.map((col: string) => ({ name: col, value: col }));
        setExcelColumns(columns);
      }
      
      // Save file name to file usage reference
      await updateProcedureFileUsage("IPE Testing", [{ file_search: selectedFile }]);
      await loadFileUsageReference();
    } catch {
      const testColumns = [
        "Employee Code", "Employee Name", "Designation", "Pay Month",
        "Date of Joining", "Date of Leaving", "PAN Number", "Gross Pay",
        "Net Pay", "Total Deductions", "Provident Fund", "ESI"
      ];
      const columns = testColumns.map((col: string) => ({ name: col, value: col }));
      setExcelColumns(columns);
    } finally {
      setIsLoadingExcelColumns(false);
    }
  };

  const handleMappingChange = (fieldKey: string, columnName: string) => {
    setColumnMappings(prev => ({ ...prev, [fieldKey]: columnName }));
  };

  const allFieldsMapped = FIELD_KEYS.every(f => columnMappings[f.key] && columnMappings[f.key].length > 0);

  const executeIPE = async () => {
    if (!selectedFile || !allFieldsMapped) return;
    setProcessingStatus("running");

    try {
      const customKeys = FIELD_KEYS.map(f => columnMappings[f.key] || "");
      const output_file_name = "Execution_EBE_ColumnMap.json";
      const section_list = ["Execution_EBE_ColumnMap"];

      // Save column mappings to file usage reference
      const columnEntries: FileUsageEntry[] = Object.entries(columnMappings).map(([key, value]) => ({
        [key]: value
      }));
      await updateProcedureFileUsage("IPE Testing", [
        { file_search: selectedFile },
        ...columnEntries
      ]);
      await loadFileUsageReference();

      const result = await window.payroll.run("execute_ipe_testing", {
        inputFiles: [],
        options: {
          ipe_custom_keys: customKeys,
          output_file_name,
          reference_value: selectedFile || "",
          section_list,
        },
      });

      if (result.ok) {
        setProcessingStatus("completed");
      } else {
        setProcessingStatus("error");
      }
    } catch {
      setProcessingStatus("error");
    }
  };

  // Save inference question
  const saveInferenceQuestion = async () => {
    if (!companyName) {
      setSaveError("Company name is required");
      setSaveStatus('error');
      return;
    }

    setSaveStatus('saving');
    setSaveError(null);

    try {
      // Auto-save any pending textbox content
      const documentManagers = document.querySelectorAll('[data-document-manager]');
      for (const manager of Array.from(documentManagers)) {
        const itemId = manager.getAttribute('data-item-id');
        if (itemId) {
          const autoSaveMethod = (window as any)[`autoSaveTextbox_${itemId}`];
          if (autoSaveMethod && typeof autoSaveMethod === 'function') {
            autoSaveMethod();
          }
        }
      }

      await new Promise(resolve => setTimeout(resolve, 1500));

      const areaPrefix = "EBE";
      const fileName = `Execution_${areaPrefix}_Responses.json`;
      
      let existingData: any = {};
      try {
        const downloadResult = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);
        if (downloadResult?.success && downloadResult?.data) {
          existingData = downloadResult.data;
        }
      } catch (error) {
        console.warn(`Failed to download existing ${fileName}, starting from empty`, error);
      }

      if (!existingData || typeof existingData !== "object" || Array.isArray(existingData)) {
        existingData = {};
      }

      if (!existingData.substantiveProcedures) {
        existingData.substantiveProcedures = {};
      }

      // Save inference question - query db.json for section field like CustomProcedure
      const inferenceId = "IPE-TESTING-INFERENCE";
      const sectionToFind = `Execution_${areaPrefix}_${inferenceId}`;
      let inferenceDocumentNames: string[] = [];
      try {
        if ((window as any).sharePointAPI?.loadDocumentListBySection) {
          const dbResult = await (window as any).sharePointAPI.loadDocumentListBySection(sectionToFind);
          if (dbResult?.success && dbResult?.data && Array.isArray(dbResult.data)) {
            inferenceDocumentNames = dbResult.data
              .map((file: any) => file.name || '')
              .filter((name: string) => name && name.trim().length > 0);
          }
        }
      } catch (error) {
        console.warn(`Failed to query db.json for section ${sectionToFind}:`, error);
      }

      const inferenceTextboxFromLocalStorage = localStorage.getItem(`textbox_${inferenceId}`);
      const hasInferenceTextboxContent = inferenceTextboxFromLocalStorage && inferenceTextboxFromLocalStorage.trim().length > 0;
      
      let inferenceTextResponseData = null;
      if (hasInferenceTextboxContent) {
        inferenceTextResponseData = {
          html: inferenceTextboxFromLocalStorage,
          plainText: inferenceTextboxFromLocalStorage.replace(/<[^>]*>/g, '')
        };
      } else if (inferenceQuestion.textResponse) {
        inferenceTextResponseData = inferenceQuestion.textResponse;
      }

      existingData.substantiveProcedures[inferenceId] = {
        selectedOption: "",
        textResponse: inferenceTextResponseData,
        documents: inferenceDocumentNames,
        table: null,
        lastUpdated: new Date().toISOString(),
        companyName,
      };

      const jsonString = JSON.stringify(existingData, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

      const fyYear = getFY_YEAR();
      if (!fyYear) {
        throw new Error("FY_YEAR is not configured. Please ensure you have selected an engagement.");
      }

      const uploadResult = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName,
        referenceValue: `Execution ${areaPrefix} Substantive Responses`,
        folderName: "juggernaut",
        fyYear: fyYear,
        sectionList: [`Execution_${areaPrefix}_Responses`],
      });

      if (!uploadResult || !uploadResult.success) {
        throw new Error(uploadResult?.error || "Failed to save inference question");
      }

      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      console.error("Failed to save inference question:", error);
      setSaveError(error instanceof Error ? error.message : "Unknown error");
      setSaveStatus('error');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">IPE Testing - Employee Benefits Expense</h2>
          <p className="text-gray-400">Map columns and update column map</p>
        </div>
        {onBack && (
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        )}
      </div>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-white">
              <Search className="h-5 w-5 text-blue-500" />
              Step 1: Search for Existing Files
            </CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Step 1: Search for Existing Files"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            {fileSearchStatus === "searching" && (
              <div className="flex items-center gap-2 text-blue-400">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Searching for files...</span>
              </div>
            )}
            {fileSearchStatus === "found" && (
              <span className="text-green-400 font-bold"> FOUND!</span>
            )}
            {fileSearchStatus === "not_found" && (
              <span className="text-yellow-400">No files found</span>
            )}
            {fileSearchStatus === "idle" && (
              <span className="text-gray-400">Searching for files...</span>
            )}
          </div>

          {fileSearchStatus === "found" && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-white">Select File</Label>
                <Select value={selectedFile} onValueChange={setSelectedFile}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select file" />
                  </SelectTrigger>
                  <SelectContent className="border-white/10 bg-black/90 text-white">
                    {foundFiles.map((f) => (
                      <SelectItem key={f} value={f}>
                        {f}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {fileUsageReference.find(e => e.file_search) && (
                  <p className="text-xs text-white/60 mt-1">
                    Last used: {fileUsageReference.find(e => e.file_search)?.file_search}
                  </p>
                )}
              </div>
              {selectedFile && (
                <div className="flex items-center gap-3">
                  <Button onClick={loadExcelColumns} disabled={!selectedFile || isLoadingExcelColumns} className="bg-blue-600 hover:bg-blue-700 text-white">
                    {isLoadingExcelColumns ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading Columns...</>
                    ) : (
                      "Load Columns"
                    )}
                  </Button>
                  {excelColumns.length > 0 && (
                    <span className="text-green-400 text-sm"> {excelColumns.length} columns loaded</span>
                  )}
                </div>
              )}
            </div>
          )}

          {fileSearchStatus === "not_found" && (
            <div className="space-y-4 p-4 border border-yellow-500/20 bg-yellow-500/5 rounded-lg">
              <div className="text-yellow-400 font-medium">File not found. Please upload a file.</div>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label className="text-white">Reference Value (optional)</Label>
                  <Input
                    value={referenceValue}
                    onChange={(e) => setReferenceValue(e.target.value)}
                    placeholder="Enter reference value"
                    className="border-white/10 bg-black/40 text-white"
                  />
                </div>
                <Button onClick={uploadFile} disabled={uploadStatus === "uploading"} className="bg-green-600 hover:bg-green-700 text-white">
                  {uploadStatus === "uploading" ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...</>
                  ) : (
                    <><Upload className="h-4 w-4 mr-2" /> Upload File</>
                  )}
                </Button>
                {uploadStatus === "completed" && (
                  <span className="text-green-400">File uploaded successfully!</span>
                )}
                {uploadStatus === "error" && (
                  <span className="text-red-400">Upload failed</span>
                )}
              </div>
            </div>
          )}

        </CardContent>
      </Card>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">Step 2: Map Columns</CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Step 2: Map Columns"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {FIELD_KEYS.map(({ key: fieldKey, label: fieldLabel }) => (
              <div key={fieldKey} className="space-y-2">
                <Label className="text-white">{fieldLabel}</Label>
                <Select
                  value={columnMappings[fieldKey] || ""}
                  onValueChange={(v) => handleMappingChange(fieldKey, v)}
                  disabled={excelColumns.length === 0}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={excelColumns.length === 0 ? "Load columns first" : "Select column"} />
                  </SelectTrigger>
                  <SelectContent className="border-white/10 bg-black/90 text-white">
                    {excelColumns.map((col) => (
                      <SelectItem key={col.value} value={col.value}>{col.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {fileUsageReference.find(e => e[fieldKey]) && (
                  <p className="text-xs text-white/60 mt-1">
                    Last used: {fileUsageReference.find(e => e[fieldKey])?.[fieldKey]}
                  </p>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-white">
              <Play className="h-5 w-5 text-green-500" />
              Execute IPE Mapping
            </CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Execute IPE Mapping"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <Button onClick={executeIPE} disabled={isArchivalMode || !selectedFile || !allFieldsMapped || processingStatus === "running"} className="bg-green-600 hover:bg-green-700 text-white">
              {processingStatus === "running" ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Processing...</>
              ) : (
                "Run IPE Testing"
              )}
            </Button>
            {processingStatus === "completed" && (
              <span className="text-green-400">Successful</span>
            )}
            {processingStatus === "error" && (
              <span className="text-red-400">Failed</span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Inference and Conclusion Question */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">Document the inference and conclusion from the above procedure performed.</CardTitle>
            <div className="flex items-center gap-2">
              {saveStatus === 'saved' && (
                <span className="text-sm text-green-400">Saved!</span>
              )}
              {saveStatus === 'error' && saveError && (
                <span className="text-sm text-red-400">Error</span>
              )}
              <Button
                onClick={saveInferenceQuestion}
                disabled={saveStatus === 'saving' || !companyName}
                size="sm"
                className="bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
              >
                {saveStatus === 'saving' ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <DocumentManager
            sectionKey="payroll"
            itemId="IPE-TESTING-INFERENCE"
            itemQuestion="Document the inference and conclusion from the above procedure performed."
            itemTextResponse={inferenceQuestion.textResponse}
            itemDocuments={inferenceQuestion.documents || []}
            onTextResponseChange={(textResponse) => setInferenceQuestion({ ...inferenceQuestion, textResponse })}
            onDocumentsChange={(documents) => setInferenceQuestion({ ...inferenceQuestion, documents })}
            disabled={false}
          />
        </CardContent>
      </Card>

      {/* Review Comment Modal */}
      {reviewCommentQuestionId && (
        <ReviewComment
          questionId={reviewCommentQuestionId}
          questionText={(() => {
            // Map questionId back to section name for display
            const sectionMap: Record<string, string> = {
              [getSectionQuestionId("Step 1: Search for Existing Files")]: "Step 1: Search for Existing Files",
              [getSectionQuestionId("Step 2: Map Columns")]: "Step 2: Map Columns",
              [getSectionQuestionId("Execute IPE Mapping")]: "Execute IPE Mapping",
            };
            return sectionMap[reviewCommentQuestionId] || reviewCommentQuestionId;
          })()}
          isOpen={!!reviewCommentQuestionId}
          onClose={() => setReviewCommentQuestionId(null)}
        />
      )}
    </div>
  );
}